import {Injectable} from '@angular/core';
import {ApiCollection, ApiResponse} from '@core/types';
import {HttpService} from '@core/services';
import {ENDPOINTS} from '@compliants-client/config';
import {GET_URL, HTTP_STATUS, log} from '@core/utils';
import {ENDPOINTS as AUTH_ENDPOINTS} from '@auth/utils';
import {HttpHeaders} from '@angular/common/http';
import {
  ChannelMessage,
  ClientUserLoginResponse,
  CompliantClientConfig,
  CompliantCreatePayload,
  CompliantFaq,
  CompliantSummary
} from '@compliants-client/types';

@Injectable({
  providedIn: 'root'
})
export class CompliantsClientService {
  private channelConfigCache: CompliantClientConfig | null = null;
  private faqsCache: CompliantFaq[] | null = null;

  constructor(private apiService: HttpService) {}

  async loginClientUser(companyId: string, code: string, password: string): Promise<ApiResponse<ClientUserLoginResponse>> {
    try {
      const url = GET_URL(AUTH_ENDPOINTS.LOGIN, {}, false);
      const headers = new HttpHeaders().set('X-Skip-Loader', '1');
      const response: ClientUserLoginResponse = await this.apiService.post(
        url,
        {identifier: code, password},
        {skipAuthLogout: true, headers}
      );
      return {success: true, result: response};
    } catch (error: any) {
      log.error('Error during client login:', error);
      const status = String(error?.code || '');
      if (status === String(HTTP_STATUS.UNAUTHORIZED)) {
        return {success: false, message: '🔒 Credenciales incorrectas. Por favor verifica tu código y contraseña.'}
      }

      if (status === String(HTTP_STATUS.UNPROCESSABLE_ENTITY) || status === String(HTTP_STATUS.BAD_REQUEST)) {
        const violation = this.extractViolationMessage(error);
        if (violation) {
          return {success: false, message: violation};
        }
      }
    }
    return {
      success: false,
      message: '🚫 Lo sentimos, no pudimos procesar tu solicitud. Por favor intenta nuevamente más tarde.'
    }
  }

  async getChannelConfig(companyId: string): Promise<ApiResponse<CompliantClientConfig>> {
    if (this.channelConfigCache) {
      return {success: true, result: this.channelConfigCache};
    }
    try {
      const url = GET_URL(ENDPOINTS.COMPLIANT_CHANNEL_CONFIG, { companyId }, false);
      const response: CompliantClientConfig = await this.apiService.get(url, {skipAuthLogout: true});
      this.channelConfigCache = response;
      return {success: true, result: response};
    } catch (error: any) {
      log.error('Error loading channel config:', error);
    }
    return {success: false, message: 'No se pudo cargar la configuracion del canal.'}
  }

  async getFaqs(companyId: string): Promise<ApiResponse<CompliantFaq[]>> {
    if (this.faqsCache) {
      return {success: true, result: this.faqsCache};
    }
    try {
      const url = GET_URL(ENDPOINTS.COMPLIANT_FAQS, {companyId}, false);
      const response: ApiCollection<CompliantFaq> = await this.apiService.get(url, {skipAuthLogout: true});
      const collection = this.apiService.normalizeCollection<CompliantFaq>(response);
      this.faqsCache = collection.member;
      return {success: true, result: collection.member};
    } catch (error: any) {
      log.error('Error loading faqs:', error);
    }
    return {success: false, message: 'No se pudieron cargar las preguntas frecuentes.'}
  }

  async createCompliant(companyId: string, payload: CompliantCreatePayload): Promise<ApiResponse<{id: string | number; code: string; createdAt: string;}>> {
    try {
      const url = GET_URL(ENDPOINTS.CLIENT_SIGNUP, {}, false);
      const headers = new HttpHeaders().set('X-Skip-Loader', '1');
      const response: {id: string | number; code: string; createdAt: string;} = await this.apiService.post(
        url,
        { ...payload, companyId },
        {skipAuthLogout: true, headers}
      );
      return {success: true, result: response};
    } catch (error: any) {
      log.error('Error creating compliant:', error);
      return {success: false, message: error.violations?.length ? error.violations[0].message : 'No se pudo crear la denuncia.'}
    }
  }

  async getCompliant(companyId: string, compliantId: string | number, token?: string | null): Promise<ApiResponse<CompliantSummary>> {
    try {
      const url = GET_URL(ENDPOINTS.COMPLIANT_DETAIL, {compliantId, companyId}, false);
      const response: any = await this.apiService.get(url, {token, skipAuthLogout: true});
      return {success: true, result: this.mapCompliantSummary(response)};
    } catch (error: any) {
      log.error('Error loading compliant:', error);
    }
    return {success: false, message: 'No se pudo cargar la denuncia.'}
  }

  async getCompliantChannel(companyId: string, compliantId: string | number, token?: string | null): Promise<ApiResponse<{channelId: string | number | null;}>> {
    try {
      const url = GET_URL(ENDPOINTS.COMPLIANT_CHANNEL, {compliantId, companyId}, false);
      const headers = new HttpHeaders().set('X-Skip-Error', '1');
      const response: {channelId: string | number;} = await this.apiService.get(url, {token, skipAuthLogout: true, headers});
      return {success: true, result: response};
    } catch (error: any) {
      log.error('Error loading compliant channel:', error);
      if (String(error?.code) === String(HTTP_STATUS.NOT_FOUND)) {
        return {success: true, result: {channelId: null}};
      }
    }
    return {success: false, message: 'No se pudo cargar el canal de la denuncia.'}
  }

  async getMessages(companyId: string, channelId: string | number, page = 1, itemsPerPage = 50, token?: string | null): Promise<ApiResponse<ChannelMessage[]>> {
    try {
      const url = GET_URL(ENDPOINTS.CHANNEL_MESSAGES, {channelId, page, itemsPerPage, companyId}, false);
      const response: ApiCollection<ChannelMessage> = await this.apiService.get(url, {token, skipAuthLogout: true});
      return {success: true, result: this.apiService.normalizeCollection<ChannelMessage>(response).member};
    } catch (error: any) {
      log.error('Error loading channel messages:', error);
    }
    return {success: false, message: 'No se pudieron cargar los mensajes.'}
  }

  async sendMessage(companyId: string, channelId: string | number, content: string, token?: string | null): Promise<ApiResponse<{id: string | number; createdAt: string;}>> {
    try {
      const url = GET_URL(ENDPOINTS.CHANNEL_MESSAGES, {channelId, companyId}, false);
      const headers = new HttpHeaders().set('X-Skip-Loader', '1');
      const response: {id: string | number; createdAt: string;} = await this.apiService.post(
        url,
        {content, companyId},
        {token, skipAuthLogout: true, headers}
      );
      return {success: true, result: response};
    } catch (error: any) {
      log.error('Error sending message:', error);
    }
    return {success: false, message: 'No se pudo enviar el mensaje.'}
  }

  async sendCompliantMessage(companyId: string, compliantId: string | number, content: string, token?: string | null): Promise<ApiResponse<{id: string | number; createdAt: string; channelId?: string | number;}>> {
    try {
      const url = GET_URL(ENDPOINTS.COMPLIANT_MESSAGES, {compliantId, companyId}, false);
      const headers = new HttpHeaders().set('X-Skip-Loader', '1');
      const response: {id: string | number; createdAt: string; channelId?: string | number;} =
        await this.apiService.post(url, {content, companyId}, {token, skipAuthLogout: true, headers});
      return {success: true, result: response};
    } catch (error: any) {
      log.error('Error sending compliant message:', error);
    }
    return {success: false, message: 'No se pudo enviar el mensaje.'}
  }

  private mapCompliantSummary(item: any): CompliantSummary {
    const status = item.status || item.state || item.phase || '';
    const normalizedStatus = status === 'submited' ? 'admission' : status;
    return {
      id: item.id,
      code: item.code || item.identifier || '',
      title: item.title || item.name || item.subject || 'Denuncia',
      status: normalizedStatus || 'admission',
      createdAt: item.createdAt || item.created_at || item.created || '',
      updatedAt: item.updatedAt || item.updated_at || item.modified || item.createdAt || ''
    };
  }

  async downloadReceipt(companyId: string, compliantId: string | number, token?: string | null): Promise<ApiResponse<Blob>> {
    try {
      const url = GET_URL(ENDPOINTS.COMPLIANT_RECEIPT, {compliantId, companyId}, false);
      const response: Blob = await this.apiService.get(url, {token, responseType: 'blob', skipAuthLogout: true});
      return {success: true, result: response};
    } catch (error: any) {
      log.error('Error downloading receipt:', error);
    }
    return {success: false, message: 'No se pudo descargar el resguardo.'}
  }

  private extractViolationMessage(error: any): string | null {
    const details = error?.details;
    if (Array.isArray(details) && details.length) {
      const message = details[0]?.message;
      return message ? String(message) : null;
    }
    if (Array.isArray(details?.violations) && details.violations.length) {
      const message = details.violations[0]?.message;
      return message ? String(message) : null;
    }
    return null;
  }
}
